import SpatialAdjustEntityType = ToroEnums.SpatialAdjustEntityType;

import { AuthManagerService } from '../auth/auth-manager.service';
import { BroadcastService } from '../../common/services/broadcast.service';
import { CloudServiceBase } from '../lynx-cloud/cloud-service-base';
import { CorrelationService } from '../../common/services/correlation.service';
import { ILynxProxyService } from '../lynx-proxy/ILynxProxyService';
import { Injectable } from '@angular/core';
import { IotChangePercentAdjustParams } from './models/iot-change-percent-adjust-params';
import { IotRequestParams } from './models/iot-request-params.model';
import { LoggerService } from '../../common/services/logger.service';
import { LynxCloudCourseItem } from '../../core/dashboard/widgets/lynx/widget-lynx-cloud/models/lynx-cloud-course-item.model';
import { LynxDriveApiService } from './lynx-drive-api.service';
import { ToroEnums } from '../../common/enumerations/toro.enums';
import { TranslateService } from '@ngx-translate/core';


@Injectable({
    providedIn: 'root'
})
export class LynxDriveService extends CloudServiceBase implements ILynxProxyService {

    private readyStartTime: number | null = null;
    private readonly READY_WAIT_MS = 5000;

    // =========================================================================================================================================================
    // Ctor and Lifecycle Hooks
    // =========================================================================================================================================================

    constructor(protected authManager: AuthManagerService,
                protected broadcastService: BroadcastService,
                protected correlationService: CorrelationService,
                private lynxDriveApiService: LynxDriveApiService,
                protected logger: LoggerService,
                protected translate: TranslateService,
    ) {
        super(authManager, broadcastService, correlationService, logger, translate);

        this.checkReadyState();
    }

    private checkReadyState() {
        const now = Date.now();

        // Initialize start time
        if (this.readyStartTime === null) {
            this.readyStartTime = now;
        }

        // Require both: user authenticated AND enough time elapsed.
        // For Lynx Drive we need to wait a bit to ensure SignalR connection is established.
        const timeElapsed = now - this.readyStartTime >= this.READY_WAIT_MS;

        if (!this.authManager.dashAuthenticatedUser || !timeElapsed) {
            setTimeout(() => this.checkReadyState(), 1000);
            return;
        }

        this.isInitialized = true;
    }

    // =========================================================================================================================================================
    // Public Interface Members
    // =========================================================================================================================================================

    // Used by Spatial Adjust
    public requestChangePercentAdjust(entityType: SpatialAdjustEntityType = SpatialAdjustEntityType.ProgramStation, percentAdjust: number, ids: number[]): void {
        if (this.spatialAdjustCourseItem == null) throw new Error("LynxCloud course information is not set.");

        const params = new IotChangePercentAdjustParams({
            installationId: this.spatialAdjustCourseItem.installationId,
            siteId: this.siteId,
            entityType: entityType,
            percentAdjust: percentAdjust,
            ids: ids
        })

        this.lynxDriveApiService.requestChangePercentAdjust(params)
            .subscribe({
                next: () => {
                    /** No action */
                },
                error: (err) => {
                    this.logger.error('Error requesting Lynx Drive Change Percent Adjust', err);
                }
            })
    }

    // =========================================================================================================================================================
    // Base Class Overrides
    // =========================================================================================================================================================

    protected override requestPingLynx(courseItem: LynxCloudCourseItem): void {
        this.lynxDriveApiService.requestPing(new IotRequestParams(courseItem?.installationId, this.siteId))
            .subscribe({
                next: () => {
                    /** No action */
                },
                error: (err) => {
                    this.logger.error('Error requesting Lynx Ping', err);
                }
            })
    }

    protected override requestCoursesInternal() {
        this.installationIds.forEach(id => {
            this.lynxDriveApiService.requestCourses(new IotRequestParams(id, this.siteId))
                .subscribe({
                    next: () => {
                        /** No action */
                    },
                    error: (err) => {
                        this.logger.error('Error requesting Lynx Drive courses', err);
                    }
                })
        });
    }

    protected override requestStations(courseItem: LynxCloudCourseItem): void {
        this.lynxDriveApiService.requestStations(courseItem.courseId, (new IotRequestParams(courseItem.installationId, this.siteId)))
            .subscribe({
                next: () => {
                    /** No action */
                },
                error: (err) => {
                    this.logger.error('Error requesting Lynx Drive stations', err);
                }
            })
    }

    protected override requestProgramTopo(courseItem: LynxCloudCourseItem): void {
        this.lynxDriveApiService.requestProgramTopo(courseItem.courseId, (new IotRequestParams(courseItem.installationId, this.siteId)))
            .subscribe({
                next: () => {
                    /** No action */
                },
                error: (err) => {
                    this.logger.error('Error requesting Lynx Drive Program Topo', err);
                }
            })
    }

    protected override requestPercentAdjustments(courseItem: LynxCloudCourseItem): void {
        this.lynxDriveApiService.requestPercentAdjustments(courseItem.courseId, (new IotRequestParams(courseItem.installationId, this.siteId)))
            .subscribe({
                next: () => {
                    /** No action */
                },
                error: (err) => {
                    this.logger.error('Error requesting Lynx Drive Percent Adjustments', err);
                }
            })
    }

    protected override requestGeoTopography(courseItem: LynxCloudCourseItem): void {
        this.lynxDriveApiService.requestGeoTopo(courseItem.courseId, (new IotRequestParams(courseItem.installationId, this.siteId)))
            .subscribe({
                next: () => {
                    /** No action */
                },
                error: (err) => {
                    this.logger.error('Error requesting Lynx Drive Geo Topo', err);
                }
            })
    }

    protected override requestCourseReport(courseItem: LynxCloudCourseItem): void {
        this.lynxDriveApiService.requestCourseReport(courseItem.courseId, (new IotRequestParams(courseItem.installationId, this.siteId)))
            .subscribe({
                next: () => {
                    /** No action */
                },
                error: (err) => {
                    this.logger.error('Error requesting Lynx Drive Course Report', err);
                }
            })
    }

}
